import React, {useEffect, useRef, useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import Button from "../../../../components/button/button.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import ContactIdService from "../../../../services/web/contact-id-service.js";
import InputRadio from "../../../../components/input/input-radio.jsx";
import AlarmPartitions from "./components/alarm-partitions.jsx";
import {FaArrowsRotate} from "react-icons/fa6";
import AlarmUsers from "./components/alarm-users.jsx";
import {useParams} from "react-router";
import AlarmSectors from "./components/alarm-sectors.jsx";
import AlarmCameras from "./components/alarm-cameras.jsx";
import {getCities, getLocationById, getNeighborhoods, getStreets} from "../../../../utils/location-utils.js";
import OptionsUtils from "../../../../utils/options-utils.js";
import useFetchData from "../../sos/citizen/form/hooks/use-fetch-data.js";
import {validateContactId} from "../utils/contact-id-validation.jsx";
import ModalAddress from "../../../../components/modal/modal-address.jsx";
import {useQuery} from "@tanstack/react-query";
import groupService from "../../../../services/web/group-service.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import MySwal from "sweetalert2";
import Dropzone from "../../../../components/dropzone/dropzone.jsx";
import AlertMessages from "../../../../components/messages/alert-message.js";
import projectUtils, {clearRequiredFieldError} from "../../../../utils/project-utils.js";
import {FaPlus, FaPrint} from "react-icons/fa";

const alarmCentralOptions = [
    { value: "Intelbras", label: "Intelbras" },
    { value: "JFL", label: "JFL" },
    { value: "Visonic", label: "Visonic" },
    { value: "Radioenge", label: "Radioenge" }
];

const fireCentralOptions = [
    { value: "Intelbras (GW 521 - webhook)", label: "Intelbras (GW 521 - webhook)" }
];

const initialData = {
    address: {
        place: null,
        description: "",
        type: null,
        street: null,
        number: "",
        complement: "",
        neighborhood: null,
        city: null,
        latitude: "",
        longitude: "",
        contact: ""
    },
    centralType: "",
    alarm: {
        driver: "",
        ip: "",
        port: "",
        deviceId: "",
        users: [],
        sectors: [],
        partitions: [],
        cameras: {
            quantityLines: 1,
            quantityColumns: 1,
            driver: "",
            vmsCameras: [],
            sectors: []
        }
    },
    fire: {
        account: "",
        fireCenterDriver: "Intelbras (GW 521 - webhook)",
        fireCenterKey: "",
        fireCenterTestInterval: 60,
        fireCenterOperationsGroup: [],
        accounts: []
    },
    files: [],
};

const ContactIdForm = () => {
    const { id } = useParams();
    const fetchData = useFetchData();
    const navigate = navigateHook();

    const [data, setData] = useState(initialData);
    const [contactIdInfo, setContactIdInfo] = useState(initialData);
    const [openMap, setOpenMap] = useState(false);
    const [attaches, setAttaches] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [attachesDescription, setAttachesDescription] = useState({});
    const [accountsRemoved, setAccountsRemoved] = useState([]);
    const centralTypeRequiredRef = useRef(null);
    const descriptionRequiredRef = useRef(null);
    const [skipPlaceRequiredValidation, setSkipPlaceRequiredValidation] = useState(false);

    const hasCoordinateValue = (value) => value !== null && value !== undefined && value !== "";

    useEffect(() => {
        if (data.centralType) clearRequiredFieldError(centralTypeRequiredRef.current);
    }, [data.centralType]);

    const setField = (path, value) => {
        setData(prev => {
            const copy = structuredClone(prev);
            let obj = copy;

            for (let i = 0; i < path.length - 1; i++) {
                obj = obj[path[i]];
            }

            obj[path[path.length - 1]] = value;
            return copy;
        });
    };

    const handleDriverChange = (newDriver) => {
        const currentDriver = data.alarm.driver;

        const nextDriver =
            typeof newDriver === "string"
                ? newDriver
                : newDriver?.value;

        const isIntelbras = currentDriver?.toUpperCase() === "INTELBRAS";

        const hasMultiplePartitions = (data.alarm.partitions ?? []).length > 1;

        if (isIntelbras && hasMultiplePartitions) {
            projectUtils.swalMessage("warning", "Aviso", "Novas partições disponível apenas para fabricante Intelbras.", "Ok");
            return;
        }

        setField(["alarm", "driver"], nextDriver);
    };

    const setAddressField = (field, value) => {
        const isCoordinateField = field === "latitude" || field === "longitude";
        const nextAddress = {
            ...data.address,
            [field]: value
        };
        const hasCurrentCoordinates = hasCoordinateValue(data.address.latitude)
            && hasCoordinateValue(data.address.longitude);
        const hasNextCoordinates = hasCoordinateValue(nextAddress.latitude)
            && hasCoordinateValue(nextAddress.longitude);
        const shouldClearPlace = nextAddress.place
            && (!isCoordinateField || !hasCurrentCoordinates || !hasNextCoordinates);
        const shouldEnterManualAddress =
            (shouldClearPlace && (isCoordinateField || hasNextCoordinates))
            || (!nextAddress.place && hasNextCoordinates);

        if (shouldEnterManualAddress) {
            setSkipPlaceRequiredValidation(true);
        }

        setData(prev => {
            const copy = structuredClone(prev);

            copy.address[field] = value;

            if (shouldClearPlace && copy.address.place) {
                copy.address.place = null;
            }

            return copy;
        });
    };

    const {refetch, status} = useQuery({
        queryKey: ["contact-id", id],
        enabled: !!id,
        queryFn: async () => {
            setIsLoading(true);

            const response = await ContactIdService.get(id);

            const hasPlace = Boolean(response.place?.value ?? response.place?.id);
            const hasCoordinates = hasCoordinateValue(response.latitude)
                && hasCoordinateValue(response.longitude);

            setSkipPlaceRequiredValidation(!hasPlace && hasCoordinates);

            const existingAttaches = (response.attaches ?? []).map(file => ({
                attachId: file.attachId,
                name: file.name,
                size: file.size,
                extension: file.extension,
                new: false
            }));

            setAttaches(existingAttaches);

            const descriptions = {};
            (response.attaches ?? []).forEach(file => {
                descriptions[file.attachId] = file.description ?? "";
            });
            setAttachesDescription(descriptions);

            setData({
                address: {
                    place: response.place,
                    description: response.description ?? "",
                    type: response.type ?? null,
                    street: response.address ?? "",
                    number: response.number ?? "",
                    complement: response.complement ?? "",
                    neighborhood: response.neighborhood ?? null,
                    city: response.city ?? null,
                    latitude: response.latitude ?? "",
                    longitude: response.longitude ?? "",
                    contact: response.contact ?? ""
                },
                centralType: response.fireAlarm ? "fire" : "alarm",
                alarm: {
                    driver: response.driver ?? "",
                    ip: response.ip ?? "",
                    port: response.port ? String(response.port) : "",
                    deviceId: response.deviceId ?? "",
                    users: response.users ?? [],
                    sectors: response.sectors ?? [],
                    partitions: response.accounts ?? [],
                    cameras: {
                        quantityLines: response.quantityLines ?? 1,
                        quantityColumns: response.quantityColumns ?? 1,
                        vmsCameras: (response.vmsCameras ?? []).map(cam => ({
                            id: cam.cameraId,
                            cameraId: cam.cameraId,
                            alarmSystemsCamerasId: cam.alarmSystemsCamerasId,
                            vmsId: cam.vmsId,
                            name: cam.name,
                            cameraName: cam.name,
                            position: cam.position,
                            sectors: cam.sectors ?? []
                        })),
                        sectors: []
                    }
                },
                fire: {
                    account: response.accounts?.[0]?.account ?? "",
                    fireCenterDriver: response.fireCenterDriver ?? "Intelbras (GW 521 - webhook)",
                    fireCenterKey: response.fireCenterKey ?? "",
                    fireCenterTestInterval: response.fireCenterTestInterval ?? 60,
                    fireCenterOperationsGroup: (response.fireCenterOperationsGroup ?? []).map(Number),
                    accounts: response.accounts ?? []
                },
                files: [],
            });

            setContactIdInfo({
                address: {
                    place: response.place,
                    description: response.description ?? "",
                    type: response.type ?? null,
                    street: response.address ?? "",
                    number: response.number ?? "",
                    complement: response.complement ?? "",
                    neighborhood: response.neighborhood ?? null,
                    city: response.city ?? null,
                    latitude: response.latitude ?? "",
                    longitude: response.longitude ?? "",
                    contact: response.contact ?? ""
                },
            })

            return response;
        }
    });

    useEffect(() => {
        if (!id) return;

        if (status === 'success') setIsLoading(false)
        else setIsLoading(true)
    }, [status]);

    const { data: groups = [] } = useQuery({
        queryKey: ["groups"],
        queryFn: async () => {
            return await groupService.getOptions();
        }
    });

    useEffect(() => {
        const driver = data.alarm.driver;
        if (!driver) return;
        if (data.alarm.port) return;

        const driverUpper = driver?.toUpperCase();

        if (driverUpper === "INTELBRAS") {
            setField(["alarm", "port"], "9009");
        } else {
            setField(["alarm", "port"], "");
        }
    }, [data.alarm.driver]);

    const changeLocation = (latLng) => {
        setAddressField("latitude", latLng.lat())
        setAddressField("longitude", latLng.lng())
    };

    const generateAccessKey = async () => {
        if (data.fire.fireCenterKey) return;

        try {
            const response = await ContactIdService.keyGenerator();
            setField(["fire", "fireCenterKey"], response.data);
        } catch (error) {
            console.error(error);
            projectUtils.swalErrorMessage("", "Erro ao gerar chave de acesso");
        }
    };

    const save = async () => {
        const normalizedDescription = typeof data.address.description === "string" ? data.address.description.trim() : "";

        if (!normalizedDescription) {
            projectUtils.swalErrorMessage("", "Preencha os campos obrigatórios.");
            return;
        }

        if (normalizedDescription !== data.address.description) {
            setField(["address", "description"], normalizedDescription);
        }

        clearRequiredFieldError(descriptionRequiredRef.current);

        const valid = validateContactId({
            address: {
                ...data.address,
                description: normalizedDescription
            },
            centralType: data.centralType,
            alarmCentral: data.centralType === "alarm" ? data.alarm : null,
            fireCentral: data.centralType === "fire" ? data.fire : null,
            isUpdate: !!id
        });

        if (!valid) return;

        const formData = new FormData();

        const isFire = data.centralType === "fire";

        const sectors = (data.alarm.sectors || []).length
            ? data.alarm.sectors
            : null;

        const vmsCamerasFormatted = !isFire
            ? (data.alarm.cameras.vmsCameras || []).map((cam) => ({
                alarmSystemsCamerasId: cam.alarmSystemsCamerasId ?? null,
                vmsId: cam.vmsId ?? null,
                cameraId: cam.id ?? null,
                name: cam.name ?? null,
                position: cam.position ?? null,
                sectors: (cam.sectors || []).map(s =>
                    typeof s === "string" ? s : s.value ?? s.id ?? ""
                )
            }))
            : [];

        const newFiles = attaches.filter(a => a.new);
        const oldFiles = attaches.filter(a => !a.new);

        const getDescription = (attach) =>
            attachesDescription[attach.attachId] ??
            attachesDescription[attach.name] ??
            null;

        const attachesPayload = oldFiles.map(attach => ({
            attachId: attach.attachId ?? null,
            name: attach.name ?? null,
            extension: attach.name?.split(".").pop() ?? null,
            size: attach.size ?? null,
            description: getDescription(attach)
        }));

        const attachmentDescription = newFiles.map(file => ({
            name: file.name ?? null,
            description: getDescription(file)
        }));

        const basePayload = {
            id: id ? Number(id) : null,
            description: normalizedDescription,
            type: data.address.type ?? null,
            address: data.address.street ?? null,
            neighborhood: data.address.neighborhood ?? null,
            city: data.address.city ?? null,
            number: data.address.number ?? null,
            complement: data.address.complement ?? null,
            latitude: data.address.latitude ? parseFloat(data.address.latitude) : null,
            longitude: data.address.longitude ? parseFloat(data.address.longitude) : null,
            placeId: data.address.place?.value ?? data.address.place?.id ?? null,
            contact: data.address.contact ?? null,
            fireAlarm: isFire,
            fireCenterDriver: isFire ? data.fire.fireCenterDriver : null,
            fireCenterKey: isFire ? data.fire.fireCenterKey : null,
            fireCenterTestInterval: isFire
                ? Number(data.fire.fireCenterTestInterval || 60)
                : null,
            fireCenterOperationsGroup: isFire
                ? (data.fire.fireCenterOperationsGroup ?? [])
                : null,
            driver: isFire
                ? data.fire.fireCenterDriver
                : data.alarm.driver,
            ip: !isFire ? data.alarm.ip : null,
            port: !isFire && data.alarm.port
                ? Number(data.alarm.port)
                : null,
            users: !isFire ? (data.alarm.users ?? []) : [],
            sectors: !isFire ? sectors : null,
            quantityLines: !isFire
                ? Number(data.alarm.cameras.quantityLines || 1)
                : null,
            quantityColumns: !isFire
                ? Number(data.alarm.cameras.quantityColumns || 1)
                : null,
            driverDvr: null,
            vmsCameras: vmsCamerasFormatted
        };

        const accounts = isFire
            ? [{
                id: data.fire.accounts?.[0]?.id ?? null,
                alarmSystems: basePayload,
                account: data.fire.account,
                partition: "a",
                active: null,
                details: null,
                alarmActive: null,
                dtLastStatus: null,
                password: null,
                isPrimary: true
            }]
            : (data.alarm.partitions ?? []);

        const payload = {
            ...basePayload,
            accounts,
            attaches: attachesPayload,
            attachmentDescription,
            ...(id && accountsRemoved.length > 0
                ? { accountsRemoved }
                : {})
        };

        newFiles.forEach(file => {
            formData.append("files[]", file.file ?? file);
        });

        const formBlob = new Blob([JSON.stringify(payload)], {
            type: "application/json"
        });

        formData.append("contactid", formBlob);

        try {
            const response = id ? await ContactIdService.update(id, formData) : await ContactIdService.save(formData);

            projectUtils.success(!id);

            if (id) {
                try {
                    setIsLoading(true);
                    await refetch();
                } finally {
                    setIsLoading(false);
                }
                return;
            }

            navigateToEdit(response.data);
        } catch (error) {
            const response = error?.response?.data;
            const messageKey = typeof response === "string" ? response : response?.message;

            projectUtils.swalErrorMessage("", AlertMessages[messageKey] ?? "Não foi possível salvar.");
        }
    };
    const deletePlace = async (e) => {
        e.preventDefault()

        const info = [data.address.place?.name || data.address.place?.label, data.address.description]
            .filter(Boolean)
            .join(" - ");

        MySwal.fire({
            icon: "warning",
            title: "Excluir",
            showCancelButton: true,
            text: `Excluir o local "${info}"?`,
            confirmButtonColor: "#063A6C",
            confirmButtonText: "Sim",
            cancelButtonColor: "#c10007",
            cancelButtonText: "Não",
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                ContactIdService.delete(id)
                    .then(() => {
                        navigate(`/contact-id`);
                        projectUtils.success(true, "Excluído com sucesso");
                    })
                    .catch((error) => {
                        console.error(error);
                        projectUtils.swalErrorMessage("", "Não foi possível excluir.");
                    })
            }
        });
    }

    const newPlace = () => {
        const urlDev = "/contact-id/form";
        navigate(urlDev);

        setSkipPlaceRequiredValidation(false);
        setData(initialData);
    };

    const navigateToEdit = (id) => {
        let isBlank = false;
        const urlDev = `/contact-id/form/${id}`;
        navigate(urlDev, isBlank);
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header className={"!h-[62px] flex items-center"}>
                <Template.Title className="flex flex-col justify-center items-start h-full" browserTitle={`${contactIdInfo.address.place?.name ?? "Monitoramento patrimonial"} ${(contactIdInfo.address?.description && contactIdInfo.address?.description.length > 0) && ` - ${contactIdInfo.address?.description}`}`}>
                    { id ? contactIdInfo.address.place?.name || contactIdInfo.address?.description : "Monitoramento patrimonial" }
                    { id && contactIdInfo.address.place && <span className="text-xs">{contactIdInfo.address?.description}</span> }
                </Template.Title>

                { id && <Button target={"default"} onClick={() => newPlace()}><FaPlus /> Adicionar local</Button> }
            </Template.Header>

            <Template.Body className={"mt-[62px]"}>
                <Card>
                    <Card.Header>Endereço</Card.Header>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4">
                            <SelectComponent
                                label={`Local pré-cadastrado${skipPlaceRequiredValidation ? "" : " *"}`}
                                skipRequiredValidation={skipPlaceRequiredValidation}
                                options={fetchData.places}
                                value={fetchData.places.find(
                                    o => o.value === (data.address.place?.value ?? data.address.place?.id)
                                ) || null}
                                onChange={async (opt) => {
                                    const hasCoordinates = hasCoordinateValue(data.address.latitude)
                                        && hasCoordinateValue(data.address.longitude);

                                    if (opt?.value) {
                                        setSkipPlaceRequiredValidation(false);
                                    } else if (hasCoordinates) {
                                        setSkipPlaceRequiredValidation(true);
                                    }

                                    setData(prev => ({
                                        ...prev,
                                        address: {
                                            ...prev.address,
                                            place: opt,
                                            ...(opt?.value ? {latitude: "", longitude: ""} : {})
                                        }
                                    }));

                                    if (!opt?.value) return;

                                    try {
                                        const selectedPlaceValue = opt.value;
                                        const loc = await getLocationById(selectedPlaceValue);
                                        if (!loc) return;

                                        setData(prev => {
                                            const currentPlaceValue = prev.address.place?.value ?? prev.address.place?.id;

                                            if (String(currentPlaceValue) !== String(selectedPlaceValue)) return prev;

                                            return {
                                                ...prev,
                                                address: {
                                                    ...prev.address,
                                                    type: loc.type ?? null,
                                                    street: loc.address ?? "",
                                                    number: loc.number ?? "",
                                                    complement: loc.complement ?? "",
                                                    neighborhood: loc.neighborhood ?? null,
                                                    city: loc.city ?? null,
                                                    latitude: loc.latitude ?? "",
                                                    longitude: loc.longitude ?? ""
                                                }
                                            };
                                        });
                                    } catch {
                                        projectUtils.swalErrorMessage("", "Erro ao carregar endereço");
                                    }
                                }}
                                classNameWrapper="col-span-3"
                            />

                            <InputForm
                                id={"description"}
                                label="Descrição *"
                                value={data.address.description}
                                reference={descriptionRequiredRef}
                                onChange={e => setField(["address", "description"], e.target.value)}
                                classNameWrapper="col-span-7"
                            />

                            <SelectComponent
                                label="Tipo"
                                options={fetchData.locationTypes}
                                value={fetchData.locationTypes.find(o => o.value === data.address.type) || null}
                                onChange={opt => setField(["address", "type"], opt?.value)}
                                classNameWrapper="col-span-2"
                            />

                            <SelectComponent
                                label="Logradouro"
                                loadOptions={getStreets}
                                value={OptionsUtils.getValue(data.address.street)}
                                onChange={opt => setAddressField("street", opt?.value)}
                                isAsync
                                classNameWrapper="col-span-6"
                            />

                            <InputForm
                                id={"number"}
                                label="Número"
                                type={"number"}
                                value={data.address.number}
                                onChange={e => setAddressField("number", e.target.value)}
                                classNameWrapper="col-span-2"
                            />

                            <SelectComponent
                                label="Bairro"
                                loadOptions={getNeighborhoods}
                                value={OptionsUtils.getValue(data.address.neighborhood)}
                                onChange={opt => setAddressField("neighborhood", opt?.value)}
                                isAsync
                                classNameWrapper="col-span-2"
                            />

                            <InputForm
                                id={"complement"}
                                label="Complemento"
                                value={data.address.complement}
                                onChange={e => setAddressField("complement", e.target.value)}
                                classNameWrapper="col-span-2"
                            />

                            <SelectComponent
                                label="Município"
                                loadOptions={getCities}
                                isAsync
                                value={OptionsUtils.getValue(data.address.city)}
                                onChange={opt => setAddressField("city", opt?.value)}
                                classNameWrapper="col-span-5"
                            />

                            <InputForm
                                id={"latitude"}
                                label="Latitude *"
                                value={data.address.latitude}
                                onChange={e => setAddressField("latitude", e.target.value)}
                                type={"number"}
                                classNameWrapper="col-span-3"
                            />

                            <InputForm
                                id={"longitude"}
                                label="Longitude *"
                                value={data.address.longitude}
                                onChange={e => setAddressField("longitude", e.target.value)}
                                type={"number"}
                                classNameWrapper="col-span-3"
                            />

                            <Button target={"primary"} onClick={() => setOpenMap(true)} className="col-span-1">
                                Mapa
                            </Button>

                            <div className="col-span-12">
                                <InputTextarea
                                    label="Contato"
                                    value={data.address.contact}
                                    onChange={e => setField(["address", "contact"], e.target.value)}
                                    textAreaClass="h-[100px]"
                                />
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card className="mt-2">
                    <Card.Header>Cadastro de central</Card.Header>
                    <Card.Body>
                        { !id && (
                            <div className="relative w-fit border border-transparent rounded-lg transition-[border-color,border-width] duration-150 ease-out"
                                ref={centralTypeRequiredRef}
                                data-required-field="true"
                                data-required-has-value={data.centralType ? "true" : "false"}
                                data-required-focus="true"
                                tabIndex={-1}
                                aria-label="Cadastro de central"
                            >
                                <div className="flex gap-4">
                                    <InputRadio
                                        id={"alarm"}
                                        name={"centralType"}
                                        label="Central de alarme"
                                        required
                                        checked={data.centralType === "alarm"}
                                        onChange={() => setField(["centralType"], "alarm")}
                                    />
                                    <InputRadio
                                        id={"fireAlarm"}
                                        name={"centralType"}
                                        label="Central de incêndio"
                                        required
                                        checked={data.centralType === "fire"}
                                        onChange={() => setField(["centralType"], "fire")}
                                    />
                                </div>
                            </div>
                        )}

                        { data.centralType === "alarm" && (
                            <div className={"mt-3"}>
                                <div className="grid grid-cols-12 gap-4 mt-1">
                                    <SelectComponent
                                        label="Fabricante *"
                                        options={alarmCentralOptions}
                                        value={alarmCentralOptions.find(o => o.value === data.alarm.driver) || null}
                                        onChange={handleDriverChange}
                                        classNameWrapper="col-span-3"
                                    />

                                    <InputForm
                                        id={"alarm-ip"}
                                        label="IP"
                                        value={data.alarm.ip}
                                        onChange={e => setField(["alarm", "ip"], e.target.value)}
                                        classNameWrapper="col-span-3"
                                        disabled={data.alarm.driver?.toUpperCase() === "VISONIC" || data.alarm.driver?.toUpperCase() === "JFL" || data.alarm.driver?.toUpperCase() === "RADIOENGE"}
                                    />

                                    <InputForm
                                        id={"alarm-port"}
                                        label="Porta"
                                        value={data.alarm.port}
                                        onChange={e => setField(["alarm", "port"], e.target.value)}
                                        classNameWrapper="col-span-3"
                                        disabled={data.alarm.driver?.toUpperCase() === "VISONIC" || data.alarm.driver?.toUpperCase() === "JFL" || data.alarm.driver?.toUpperCase() === "RADIOENGE"}
                                    />

                                    <InputForm
                                        id={"alarm-device-id"}
                                        label="ID do equipamento"
                                        value={data.alarm.deviceId}
                                        disabled
                                        classNameWrapper="col-span-3"
                                    />
                                </div>

                                <AlarmPartitions
                                    id={id}
                                    partitions={data.alarm.partitions}
                                    setPartitions={p => setField(["alarm", "partitions"], p)}
                                    driver={data.alarm.driver}
                                    ipConfig={data.alarm.ip}
                                    portConfig={data.alarm.port}
                                    setIsLoading={setIsLoading}
                                    setAccountsRemoved={setAccountsRemoved}
                                />

                                <AlarmUsers
                                    users={data.alarm.users}
                                    setUsers={u => setField(["alarm", "users"], u)}
                                />

                                <AlarmSectors
                                    sectors={data.alarm.sectors}
                                    setSectors={s => setField(["alarm", "sectors"], s)}
                                />

                                <AlarmCameras
                                    cameras={data.alarm.cameras}
                                    setCameras={c => setField(["alarm", "cameras"], {...data.alarm.cameras, ...c})}
                                    sectors={data.alarm.sectors}
                                />
                            </div>
                        )}

                        { data.centralType === "fire" && (
                            <div className={"mt-3"}>
                                <div className="grid grid-cols-12 gap-4 mt-1">
                                    <SelectComponent
                                        label="Fabricante *"
                                        options={fireCentralOptions}
                                        value={fireCentralOptions.find(o => o.value === data.fire.fireCenterDriver) || null}
                                        onChange={opt => setField(["fire", "fireCenterDriver"], opt?.value)}
                                        classNameWrapper="col-span-6"
                                    />

                                    <InputForm
                                        id={"fire-center-mac-address"}
                                        label="Endereço físico (MAC Address) *"
                                        value={data.fire.account}
                                        onChange={e => setField(["fire", "account"], e.target.value)}
                                        disabled={id}
                                        classNameWrapper="col-span-6"
                                    />

                                    <div className="flex col-span-6 gap-2 items-end">
                                        <InputForm
                                            id={"fire-center-key"}
                                            label="Chave de acesso"
                                            classNameWrapper="flex-1"
                                            value={data.fire.fireCenterKey}
                                            disabled
                                        />

                                        <Button disabled={!!data.fire.fireCenterKey} onClick={generateAccessKey}>
                                            <FaArrowsRotate />
                                        </Button>
                                    </div>

                                    <InputForm
                                        id={"fire-center-interval"}
                                        label="Intervalo de teste (s)"
                                        type="number"
                                        classNameWrapper="col-span-3"
                                        value={data.fire.fireCenterTestInterval}
                                        onChange={e => setField(["fire", "fireCenterTestInterval"], e.target.value)}
                                    />

                                    <SelectComponent
                                        label={"Grupos de operação"}
                                        options={groups ?? []}
                                        value={groups.filter(g => data.fire.fireCenterOperationsGroup?.includes(g.value))}
                                        onChange={(opts) =>
                                            setField(
                                                ["fire", "fireCenterOperationsGroup"],
                                                (opts ?? []).map(o => o.value)
                                            )
                                        }
                                        isMulti
                                        classNameWrapper={"col-span-3"}
                                    />
                                </div>
                            </div>
                        )}
                    </Card.Body>
                </Card>

                <div className="mt-2">
                    <Dropzone
                        title={"Anexos"}
                        attaches={attaches}
                        setAttaches={setAttaches}
                        attachesDescription={attachesDescription}
                        setAttachesDescription={setAttachesDescription}
                    />
                </div>

                <Card className="mt-2">
                    <Card.Body className="flex justify-end gap-2">
                        { id &&
                            <Button target="danger" onClick={deletePlace} actionFeedbackType='delete'>
                                Excluir
                            </Button>
                        }

                        <Button target="primary" onClick={save} actionFeedbackType='save'>
                            Salvar
                        </Button>
                    </Card.Body>
                </Card>

                <ModalAddress
                    open={openMap}
                    setOpen={setOpenMap}
                    changeLocation={changeLocation}
                    address={{
                        ...data.address,
                        address: data.address.street
                    }}
                />
            </Template.Body>
        </Template>
    );
};

export default ContactIdForm;